import { AttributionMetadata, UserAttributionData, UTMParameters } from '@/types';
import { getStoredItem, removeStoredItem, setStoredItem } from '@/utils/store/local';

const UTM_STORAGE_KEY = 'fluentUTMParameters';
const UTM_EXPIRY_DAYS = 30;

export const UTM_PARAMS = {
  UTM_SOURCE: 'utm_source',
  UTM_MEDIUM: 'utm_medium',
  UTM_CAMPAIGN: 'utm_campaign',
  UTM_CONTENT: 'utm_content',
  UTM_TERM: 'utm_term',
  UTM_CAMPAIGNID: 'utm_campaignid',
  UTM_ADGROUPID: 'utm_adgroupid',
  UTM_DEVICE: 'utm_device',
  UTM_MATCHTYPE: 'utm_matchtype',
  UTM_ADPOSITION: 'utm_adposition',
  UTM_HSID: 'utm_hsid',
  UTM_GCLID: 'gclid',
  UTM_FBCLID: 'fbclid',
} as const;

const getCleanLandingPage = (url: string): string => {
  try {
    const urlObj = new URL(url);

    // All UTM and tracking parameters to remove to ensure clean landing page is stored
    const paramsToRemove = [...Object.values(UTM_PARAMS), 'msclkid', 'twclid', 'li_fat_id', '_ga', 'mc_cid', 'mc_eid'];

    paramsToRemove.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    return urlObj.toString();
  } catch (error) {
    console.error('Error cleaning landing page URL:', error);
    return url;
  }
};

const captureAttributionMetadata = (): AttributionMetadata => {
  if (typeof window === 'undefined') {
    return {
      landingPage: '',
      referrer: '',
      userAgent: '',
    };
  }

  const cleanLandingPage = getCleanLandingPage(window.location.href);

  return {
    landingPage: cleanLandingPage,
    referrer: document.referrer || '',
    userAgent: navigator.userAgent || '',
  };
};

export const storeUTMParameters = (utmParams: UTMParameters): void => {
  const dataToStore: UserAttributionData = {
    ...utmParams,
    capturedAt: new Date().toISOString(),
    sessionId: generateSessionId(),
    expiresAt: new Date(Date.now() + UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    attributionMetadata: captureAttributionMetadata(),
  };

  setStoredItem(UTM_STORAGE_KEY, dataToStore);
};

export const getStoredUTMParameters = (): UserAttributionData | null => {
  try {
    const data = getStoredItem(UTM_STORAGE_KEY) as UserAttributionData | null;

    if (!data) return null;

    if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
      removeStoredItem(UTM_STORAGE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Failed to retrieve UTM parameters:', error);
    removeStoredItem(UTM_STORAGE_KEY); // Clean up corrupted data
    return null;
  }
};

export function clearStoredUTMParameters(): void {
  removeStoredItem(UTM_STORAGE_KEY);
}

export const shouldCaptureUTM = (newParams: UTMParameters): boolean => {
  const existing = getStoredUTMParameters();

  if (!existing) return true;

  const keyFields = ['utmSource', 'utmMedium', 'utmCampaign'] as const;
  return keyFields.some((field) => existing[field] !== newParams[field]);
};

const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
